import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { useSetState } from '@domain-group/react-hooks'

import type { DefaultContext } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import { digObject, getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import generateNavigationItems from
  '@sections/Client/packs/Accounting/components/AccountingNavigation/utils/generateNavigationItems'

import AccountingIntegrationButton from '@components/AccountingIntegrationButton'
import useAccountingIntegration from '@hooks/useAccountingIntegration'
import useAccountingItems from '../../hooks/useAccountingItems'

import type { PageContextPayload } from '../../index'

import InvoicesTools from '../InvoicesTools'
import ListView from '../ListView'
import TableView from '../TableView'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        label: 'Filter by Address',
        entityKey: 'campaigns',
      },
      key: 'campaign_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Invoice ID, Debtor Name or Reference',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'start_date',
      label: 'Filter by Created After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'end_date',
      label: 'Filter by Created Before',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'approved',
      label: 'Approved Status',
      values: [
        { key: 'true', label: 'Approved' },
        { key: 'false', label: 'Unapproved' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'paid',
      label: 'Payment Status',
      values: [
        { key: 'true', label: 'Paid' },
        { key: 'false', label: 'Unpaid' },
      ],
    },
  ]
}

const defaultState = {
  viewMode: 'table',
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const [state, setState] = useSetState(defaultState)
  const { viewMode } = state

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  // @ts-expect-error TS2339
  const { type }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['type'])
  const invoiceType = type || 'creditor'

  const accountingIntegrationPayload = useAccountingIntegration({
    shouldFetchAccounts: true,
    shouldFetchConnections: true,
    shouldFetchIntegrationData: true,
  })

  const {
    hasActiveSession,
    loadingIntegrationData,
    selectedAgencyId,
    selectedIntegrationId,
  } = accountingIntegrationPayload

  const filters = {
    agency_id: selectedAgencyId,
    creditor_type: invoiceType === 'creditor' ? 'Agency' : undefined,
    debtor_type: invoiceType === 'debtor' ? 'Agency' : undefined,
    ...pageFilters,
  }

  const accountingItemsPayload = useAccountingItems({
    // @ts-expect-error TS2322
    filters,
    performHttpRequests: !!selectedAgencyId,
    requestOptions: {
      include_credit_note_campaign: true,
      include_invoice_campaign: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAccountingItems,
    filteredAccountingItemsCount,
    hasFilteredAccountingItems,
    loading: loadingAccountingItems,
  } = accountingItemsPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  // @ts-expect-error TS2345
  const nav = generateNavigationItems(selectedAgencyId)
  const items = digObject(nav.sections[0], 'items')

  const loading = loadingIntegrationData || loadingAccountingItems

  return (
    <>
      <Helmet>
        <title>Invoices | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          (!selectedIntegrationId || hasActiveSession) && (
            // @ts-expect-error TS2741
            <AccountingIntegrationButton accountingPayload={accountingIntegrationPayload} />
          )
        )}
        activeTabBarItemKey={invoiceType === 'creditor' ? 'invoices' : 'bills'}
        nestedNavigation
        tabBarItems={items}
        title="Invoices"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.invoices.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAccountingItems && <ListBlankState />}

            {!loading && hasFilteredAccountingItems && viewMode === 'table' && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Invoices">
                <Box flexDirection="column">
                  <TableView
                    accountingItems={filteredAccountingItems}
                    accountingIntegrationPayload={accountingIntegrationPayload}
                  />
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredAccountingItemsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}

            {!loading && hasFilteredAccountingItems && viewMode === 'list' && (
              <>
                <ListView
                  accountingItems={filteredAccountingItems}
                  accountingIntegrationPayload={accountingIntegrationPayload}
                />

                {canLoadMore && (
                  <DashboardModule.LoadMoreFooter
                    boxProps={{ marginBottom: 'large', variant: 'white' }}
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredAccountingItemsCount}
                    loading={loading}
                  />
                )}
              </>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.invoices.sidebar" />

            <InvoicesTools
              callbacks={{ setState }}
              hasActiveSession={hasActiveSession}
              // @ts-expect-error TS2322
              selectedAgencyId={selectedAgencyId}
              viewMode={viewMode}
            />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
